import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useDomain } from '../context/DomainContext';
import { RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../config/firebase';
import Swal from 'sweetalert2';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInputUtils from 'intl-tel-input/utils';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const { signIn, user } = useAuth();
  const { shortName, displayName } = useDomain();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneInputRef = useRef(null);
  const phoneInstanceRef = useRef(null);

  // Guardar la URL de redirección al cargar el componente
  const [redirectTo, setRedirectTo] = useState('/');

  useEffect(() => {
    // Obtener y guardar la URL de redirección del estado
    const from = location.state?.from || '/';
    console.log('Redirect URL:', from);
    setRedirectTo(from);
  }, [location]);

  useEffect(() => {
    // Crear el recaptcha al montar el componente
    const verifier = new RecaptchaVerifier('recaptcha-container', {
      size: 'invisible'
    }, auth);
    
    setRecaptchaVerifier(verifier);

    // Limpiar al desmontar
    return () => {
      verifier.clear();
    };
  }, []);

  useEffect(() => {
    if (phoneInputRef.current) {
      const init = async () => {
        // Esperar a que se cargue el script de utils
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js';
          script.onload = resolve;
          document.body.appendChild(script);
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        const iti = intlTelInput(phoneInputRef.current, {
          preferredCountries: ['es'],
          initialCountry: 'es',
          separateDialCode: true,
          utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
          dropdownContainer: document.body,
          nationalMode: false,
          formatOnDisplay: false,
          autoPlaceholder: 'aggressive',
          customPlaceholder: function() {
            return "666 777 888";
          }
        });

        // Manejar el cambio de país
        phoneInputRef.current.addEventListener('countrychange', function() {
          const currentValue = phoneInputRef.current.value;
          // Limpiar solo los números, manteniendo cualquier otro carácter
          const cleanNumber = currentValue.replace(/\d/g, '');
          phoneInputRef.current.value = cleanNumber;
          
          // Forzar un refresco del input
          setTimeout(() => {
            const event = new Event('input');
            phoneInputRef.current.dispatchEvent(event);
          }, 0);
        });

        phoneInstanceRef.current = iti;
      };

      init();

      return () => {
        if (phoneInstanceRef.current) {
          phoneInstanceRef.current.destroy();
        }
      };
    }
  }, []);

  // Efecto para manejar la redirección cuando el usuario está autenticado
  useEffect(() => {
    if (user) {
      console.log('Usuario autenticado, redirigiendo a:', redirectTo);
      navigate(redirectTo, { replace: true });
    }
  }, [user, redirectTo, navigate]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (!phoneInputRef.current || !phoneInstanceRef.current) {
        throw new Error('Error al inicializar el input de teléfono');
      }

      const inputValue = phoneInputRef.current.value.trim();
      console.log('1. Valor del input:', inputValue);

      if (!inputValue) {
        setError('Por favor, introduce un número de teléfono');
        return;
      }

      // Esperar a que utils esté disponible
      await new Promise(resolve => setTimeout(resolve, 100));

      // Obtener el número con el prefijo del país seleccionado
      const selectedCountryData = phoneInstanceRef.current.getSelectedCountryData();
      const dialCode = selectedCountryData.dialCode;
      const phoneNumber = `+${dialCode}${inputValue.replace(/\D/g, '')}`;
      
      console.log('2. País seleccionado:', selectedCountryData.iso2);
      console.log('3. Código de país:', dialCode);
      console.log('4. Número completo:', phoneNumber);



      if (!recaptchaVerifier) {
        const verifier = new RecaptchaVerifier('recaptcha-container', {
          size: 'invisible'
        }, auth);
        setRecaptchaVerifier(verifier);
      }

      const result = await signIn(phoneNumber, recaptchaVerifier);
      setConfirmationResult(result);
      sessionStorage.setItem('phoneNumber', phoneNumber);
    } catch (error) {
      console.error('Error completo:', error);
      setError('Error al enviar el código. Por favor, intenta de nuevo.');
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!confirmationResult) {
      setError('Por favor, solicita un nuevo código de verificación');
      return;
    }

    try {
      // Validar que el código tenga 6 dígitos
      if (!/^\d{6}$/.test(verificationCode)) {
        setError('El código debe tener 6 dígitos');
        return;
      }

      await confirmationResult.confirm(verificationCode);
      sessionStorage.removeItem('phoneNumber');
    } catch (err) {
      console.error('Error al verificar el código:', err);
      
      if (err.code === 'auth/invalid-verification-code') {
        setError('Código incorrecto. Por favor, verifica el código e intenta de nuevo.');
      } else if (err.code === 'auth/code-expired') {
        setError('El código ha expirado. Por favor, solicita uno nuevo.');
        setConfirmationResult(null);
      } else {
        setError('Error al verificar el código. Por favor, intenta de nuevo.');
      }
    }
  };

  return (
    <PageContainer>
      <GradientTopBar>
        <Title>
          <MainTitle>El {shortName}</MainTitle>
          <SubTitle>de {displayName}</SubTitle>
        </Title>
      </GradientTopBar>
      <LoginContainer>
        <LoginForm onSubmit={!confirmationResult ? handleSendCode : handleVerifyCode}>
          <FormTitle>Iniciar Sesión</FormTitle>
          {!confirmationResult ? (
            <>
              <FormGroup>
                <Label>Número de teléfono</Label>
                <PhoneInputContainer>
                  <Input
                    ref={phoneInputRef}
                    type="tel"
                    required
                  />
                </PhoneInputContainer>
                <HelpText>Introduce un número de móvil español (ej: 666777888)</HelpText>
              </FormGroup>
              <ActionButton type="submit">Enviar código</ActionButton>
            </>
          ) : (
            <>
              <FormGroup>
                <Label>Código de verificación</Label>
                <VerificationInput
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  pattern="\d{6}"
                  maxLength="6"
                  required
                />
              </FormGroup>
              <ActionButton type="submit">Verificar código</ActionButton>
              <ResendButton type="button" onClick={handleSendCode}>
                Solicitar nuevo código
              </ResendButton>
            </>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div id="recaptcha-container"></div>
        </LoginForm>
      </LoginContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url('/images/sort.jpeg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: -1;
  }
`;

const GradientTopBar = styled.header`
  position: relative;
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  margin-bottom: 0.5rem;
`;

const Title = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const MainTitle = styled.h1`
  font-family: 'Fredoka One', sans-serif;
  font-size: 2.1rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 3px white;
  margin: 0;
  margin-top: 1.8rem;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const SubTitle = styled.h2`
  font-family: 'Fredoka One', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 1px white;
  margin: 0;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 0 1rem;
`;

const LoginForm = styled.form`
  background: rgba(88, 28, 135, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.2);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 400px;
`;

const FormTitle = styled.h2`
  color: #ffd700;
  font-size: 1.8rem;
  text-align: center;
  margin-bottom: 2rem;
  font-family: 'Fredoka One', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  color: rgba(255, 215, 0, 0.8);
  display: block;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-left: 90px !important;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  backdrop-filter: blur(5px);
  font-size: 18px;
  height: 55px;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 
      0 0 0 1px rgba(255, 215, 0, 0.2),
      0 0 15px rgba(255, 215, 0, 0.2);
    background: rgba(255, 255, 255, 0.15);
  }

  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
`;

const VerificationInput = styled(Input)`
  padding-left: 0.75rem !important;
  text-align: center;
  letter-spacing: 4px;
  font-family: 'Roboto Mono', monospace;
  font-size: 20px;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 179, 71, 0.2) 100%);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  letter-spacing: 0.5px;
  height: 55px;

  &:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(255, 179, 71, 0.3) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const HelpText = styled.small`
  color: rgba(255, 215, 0, 0.6);
  font-size: 1rem;
  margin-top: 0.25rem;
  display: block;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 215, 0, 0.8);
  text-decoration: underline;
  cursor: pointer;
  margin-top: 1rem;
  font-size: 0.9rem;
  width: 100%;
  padding: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    color: #ffd700;
    text-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
  }
`;

const PhoneInputContainer = styled.div`
  .iti {
    width: 100%;
    display: block;
  }
  
  .iti__flag-container {
    z-index: 10;
  }

  .iti__selected-flag {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px 0 0 4px;
    transition: all 0.3s ease;
    padding: 0 16px;
    border-right: 1px solid rgba(255, 215, 0, 0.2);
    height: 55px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    &:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .iti__selected-dial-code {
    color: rgba(255, 215, 0, 0.9);
    font-family: 'Roboto Mono', monospace;
    font-size: 0.95rem;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.2);
  }

  .iti__country-list {
    background: rgba(88, 28, 135, 0.98);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 215, 0, 0.3);
    color: #ffd700;
    border-radius: 12px;
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 215, 0, 0.1);
    max-height: 360px;
    margin-top: 8px;
    padding: 8px 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 215, 0, 0.5) rgba(88, 28, 135, 0.98);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
      margin: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 215, 0, 0.3);
      border-radius: 3px;
      border: 1px solid rgba(255, 215, 0, 0.1);

      &:hover {
        background: rgba(255, 215, 0, 0.4);
      }
    }
  }

  .iti__country {
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: all 0.2s ease;
    border-radius: 6px;
    margin: 0 4px;

    &:hover {
      background-color: rgba(255, 215, 0, 0.1);
      transform: translateX(4px);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .iti__country.iti__highlight {
    background-color: rgba(255, 215, 0, 0.15);
  }

  .iti__flag-box {
    margin-right: 10px;
    opacity: 0.9;
  }

  .iti__country-name {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
    font-weight: 500;
    flex: 1;
  }

  .iti__dial-code {
    color: rgba(255, 215, 0, 0.9);
    font-family: 'Roboto Mono', monospace;
    font-size: 0.9rem;
    padding: 2px 6px;
    background: rgba(255, 215, 0, 0.1);
    border-radius: 4px;
    margin-left: auto;
  }

  .iti__arrow {
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid rgba(255, 215, 0, 0.8);
    transition: transform 0.3s ease;
    margin-left: 8px;
  }

  .iti__arrow--up {
    transform: rotate(180deg);
  }

  input {
    padding-left: 90px !important;
  }

  // Efecto de hover para toda la bandera/prefijo
  .iti__flag-container:hover {
    .iti__selected-flag {
      background: rgba(255, 255, 255, 0.15);
    }
    .iti__selected-dial-code {
      color: #ffd700;
    }
  }
`;

export default Login; 